import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  Label,
  Input,
  Button,
} from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../../axiosConfig";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import PropTypes from "prop-types";
import { Link, withRouter } from "react-router-dom/cjs/react-router-dom.min";
import { withTranslation } from "react-i18next";
import Loader from "../../../Components/Common/Loader";
import ExportCSVModal from "../../../Components/Common/ExportCSVModal";

// Utilitaires pour formatage
function formaterMontant(montant) {
  if (!montant) return "";
  return Number(montant).toLocaleString("fr-FR") + " XOF";
}
function nomPlanAuto(
  typesEngin,
  typeEnginId,
  montantTotal,
  frequence,
  montantPaiement
) {
  const engin = typesEngin.find((e) => String(e.id) === String(typeEnginId));
  const label = engin
    ? engin.classe
      ? `${engin.designation} - ${engin.classe}`
      : engin.designation
    : "";
  if (!label || !montantTotal || !frequence || !montantPaiement) return "";
  const freq = frequence.charAt(0).toUpperCase() + frequence.slice(1);
  return `${label} - ${formaterMontant(
    montantTotal
  )} - ${freq} - ${formaterMontant(montantPaiement)}`;
}
function daysToYMD(days) {
  const years = Math.floor(days / 365);
  days %= 365;
  const months = Math.floor(days / 30);
  days %= 30;
  return [
    years ? years + (years > 1 ? " ans" : " an") : null,
    months ? months + (months > 1 ? " mois" : " mois") : null,
    days ? days + (days > 1 ? " jours" : " jour") : null,
  ]
    .filter(Boolean)
    .join(", ");
}
function addDaysToDate(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

const Plans = (props) => {
  const [plans, setPlans] = useState([]);
  const [typesEngin, setTypesEngin] = useState([]);
  const [modal, setModal] = useState(false);
  const [autoName, setAutoName] = useState(true);
  const [loading, setLoading] = useState(true);
  const [isExportCSV, setIsExportCSV] = useState(false);
  const [adding, setAdding] = useState(false);

  const [newPlan, setNewPlan] = useState({
    nom: "",
    type_engin: "",
    montant_total: "",
    frequence_paiement: "",
    montant_par_paiement: "",
  });

  // Estimation durée
  const tousChampsPourCalcul =
    newPlan.montant_total &&
    newPlan.frequence_paiement &&
    newPlan.montant_par_paiement &&
    Number(newPlan.montant_par_paiement) > 0;

  const dureeTotalEcheances = tousChampsPourCalcul
    ? Math.ceil(
        Number(newPlan.montant_total) / Number(newPlan.montant_par_paiement)
      )
    : 0;
  const dureeTotalJours =
    newPlan.frequence_paiement === "hebdomadaire"
      ? dureeTotalEcheances * 7
      : dureeTotalEcheances;
  const dureeLisible =
    dureeTotalEcheances > 0 ? daysToYMD(dureeTotalJours) : "";
  const dateDebut = new Date();
  const dateFin =
    dureeTotalJours > 0 ? addDaysToDate(dateDebut, dureeTotalJours) : null;

  // Liste des engins
  useEffect(() => {
    const fetchEngins = async () => {
      try {
        const res = await axiosInstance.get("/allengin");
        if (res.data && Array.isArray(res.data.data)) {
          setTypesEngin(res.data.data);
        }
      } catch (err) {
        toast.error("Erreur lors du chargement des types d'engin");
      }
    };
    fetchEngins();
  }, []);

  // Liste des plans
  useEffect(() => {
    const fetchPlans = async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get("/templateremboursement/");
        console.log("res.data: ", res.data);
        console.log("res: ", res);
        if (res.data && Array.isArray(res.data)) {
          setPlans(res.data);
        }
      } catch (err) {
        // toast.error("Erreur lors du chargement des plans");
        toast.warn("Aucun plan de Remboursement trouvé.");
      }
      setLoading(false);
    };
    fetchPlans();
  }, []);

  const toggleModal = () => {
    setModal(!modal);
    setAutoName(true);
    setNewPlan({
      nom: "",
      type_engin: "",
      montant_total: "",
      frequence_paiement: "",
      montant_par_paiement: "",
    });
  };

  // Ajout d'un plan avec la bonne structure attendue par l'API
  const handleAddPlan = async (e) => {
    e.preventDefault();

    // Récupérer l'objet engin sélectionné
    const enginObj = typesEngin.find(
      (e) => String(e.id) === String(newPlan.type_engin)
    );
    const jour_delai = dureeTotalEcheances; // nombre d'échéances
    const dataToSend = {
      titre: newPlan.nom,
      montant_total: Number(newPlan.montant_total),
      frequence_paiement: newPlan.frequence_paiement,
      montant_a_paye: Number(newPlan.montant_par_paiement),
      delai_fin: dateFin ? dateFin.toISOString().slice(0, 10) : "",
      engin_id: enginObj ? enginObj.id : null,
      jour_delai: jour_delai,
    };

    function daysToYMD(days) {
      const years = Math.floor(days / 365);
      days %= 365;
      const months = Math.floor(days / 30);
      days %= 30;
      return [
        years ? years + (years > 1 ? " ans" : " an") : null,
        months ? months + (months > 1 ? " mois" : " mois") : null,
        days ? days + (days > 1 ? " jours" : " jour") : null,
      ]
        .filter(Boolean)
        .join(", ");
    }

    // Validation des champs
    if (
      !dataToSend.titre ||
      !dataToSend.engin_id ||
      !dataToSend.montant_total ||
      !dataToSend.frequence_paiement ||
      !dataToSend.montant_a_paye ||
      !dataToSend.delai_fin ||
      !dataToSend.jour_delai
    ) {
      toast.error("Tous les champs sont requis.");
      return;
    }

    try {
      await axiosInstance.post("/addtemplateremboursement/", dataToSend);
      toast.success("Plan ajouté avec succès");
      toggleModal();
      // Rafraîchir la liste après ajout
      const res = await axiosInstance.get("/templateremboursement/");
      if (res.data && Array.isArray(res.data)) {
        setPlans(res.data);
      }
    } catch (err) {
      toast.error("Erreur lors de l'ajout du plan");
    }
  };

  const columns = useMemo(() => [
    { Header: "Nom", accessor: "titre" },
    { Header: "Type Engin", accessor: "type_engin", filterable: true },
    { Header: "Montant total (XOF)", accessor: "montant_total" },
    { Header: "Fréquence", accessor: "frequence_paiement", filterable: true, },
    { Header: "Montant paiement (XOF)", accessor: "montant_a_paye" },
    {
      Header: "Date de fin",
      accessor: "delai_fin",
      Cell: ({ row }) => {
        const plan = row.original;
        // On récupère le nombre d’échéances et la fréquence
        const nbEcheances = Number(plan.jour_delai) || 0;
        const frequence = plan.frequence_paiement || "";
        // Conversion en jours pour l'affichage humain
        const nbJours =
          frequence === "hebdomadaire" ? nbEcheances * 7 : nbEcheances;
        const conversion = daysToYMD(nbJours);

        return (
          <div>
            <div>
              <strong>
                {plan.delai_fin
                  ? new Date(plan.delai_fin).toLocaleDateString("fr-FR")
                  : "-"}
              </strong>
            </div>
            <div>
              <span>
                {nbEcheances}{" "}
                {frequence === "hebdomadaire" ? "semaines" : "jours"}
              </span>
            </div>
            <div>
              <small className="text-muted">{conversion}</small>
            </div>
          </div>
        );
      },
    },
    {
      Header: "Action",
      Cell: ({ row }) => (
        <ul className="justify-content-center list-inline hstack gap-2 mb-0">
          <li className="list-inline-item" title={props.t("Voir")}>
            <button
              className= "text-success border-0 bg-transparent d-inline-block"
              size="sm"
              onClick={() => {
                // Rediriger ou ouvrir la page détail
                window.location.href = `/plans-detail/${
                  row.original.plan_id || row.original.id
                }`;
              }}
              >
              <i className="ri-eye-fill align-bottom"></i>
            </button>
          </li>
        </ul>
      ),
    },
  ]);

  document.title = props.t("Plans | Takci");

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Plans de remboursement" pageTitle="Takci" />
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex justify-content-end">
                {/* <Button onClick={toggleModal} color="primary">
                 */}
                <button
                  className="btn add-btn"
                  style={{ background: "#002437", color: "white" }}
                  onClick={toggleModal}
                >
                  Ajouter un plan
                </button>
                {plans.length > 0 && (
                  <button
                    disabled={plans.length <= 0}
                    type="button"
                    className="btn"
                    style={{ color: "#eac22f" }}
                    onClick={() => setIsExportCSV(true)}
                  >
                    <i className="ri-file-download-line align-bottom me-1"></i>{" "}
                    {props.t("Export")}
                  </button>
                )}
              </CardHeader>
              {/* <CardBody> */}
                <CardBody className="pt-0">
                  <div>
                    {loading ? (
                      <div className="text-center my-5">
                        <Loader style={{ color: "#014c94" }} />
                        <h2>Chargement...</h2>
                      </div>
                    ) : (
                      <>
                        <ExportCSVModal
                          show={isExportCSV}
                          onCloseClick={() => setIsExportCSV(false)}
                          data={plans.length > 0 ? plans : []}
                        />
                        {plans.length > 0 ? (
                          <TableContainer
                            columns={columns}
                            data={plans}
                            isGlobalFilter={true}
                            isAddUserList={false}
                            customPageSize={8}
                            className="custom-header-css"
                            divClass="table-responsive table-card mb-3"
                            tableClass="align-middle table-nowrap"
                            theadClass="table-light"
                            loading={loading}
                          />
                          ) : (
                            <Card>
                              <CardBody className="p-4 border-top border-top-dashed">
                                <h6 className="text-muted text-uppercase fw-semibold mb-4">
                                  Aucune donnée disponible
                                </h6>
                              </CardBody>
                            </Card>
                          )
                        }
                      </>
                    )}
                  </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Modal isOpen={modal} toggle={toggleModal} centered>
          <ModalHeader toggle={toggleModal}>Nouveau plan</ModalHeader>
          <Form onSubmit={handleAddPlan}>
            <ModalBody>
              <Row className="g-3">
                <Col md={12}>
                  <Label>Nom du plan *</Label>
                  <Input
                    value={newPlan.nom}
                    onChange={(e) => {
                      setNewPlan({ ...newPlan, nom: e.target.value });
                    }}
                    placeholder="Nom du plan "
                    required
                  />
                </Col>
                <Col md={6}>
                  <Label>Type d'engin *</Label>
                  <Input
                    type="select"
                    value={newPlan.type_engin}
                    onChange={(e) => {
                      setNewPlan({ ...newPlan, type_engin: e.target.value });
                    }}
                    required
                  >
                    <option value="">Sélectionnez</option>
                    {typesEngin.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.classe
                          ? `${e.designation} - ${e.classe}`
                          : e.designation}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={6}>
                  <Label>Fréquence *</Label>
                  <Input
                    type="select"
                    value={newPlan.frequence_paiement}
                    onChange={(e) => {
                      setNewPlan({
                        ...newPlan,
                        frequence_paiement: e.target.value,
                      });
                    }}
                    required
                  >
                    <option value="">Choisir</option>
                    <option value="journalier">Journalier</option>
                    <option value="hebdomadaire">Hebdomadaire</option>
                  </Input>
                </Col>
                <Col md={6}>
                  <Label>Montant total (XOF) *</Label>
                  <Input
                    type="number"
                    value={newPlan.montant_total}
                    onChange={(e) => {
                      setNewPlan({ ...newPlan, montant_total: e.target.value });
                    }}
                    required
                  />
                </Col>
                <Col md={6}>
                  <Label>Montant par paiement (XOF) *</Label>
                  <Input
                    type="number"
                    value={newPlan.montant_par_paiement}
                    onChange={(e) => {
                      setNewPlan({
                        ...newPlan,
                        montant_par_paiement: e.target.value,
                      });
                    }}
                    required
                  />
                </Col>
                {tousChampsPourCalcul && (
                  <Col md={12}>
                    <div style={{ fontSize: "0.9em" }}>
                      <strong>Durée totale estimée :</strong> {dureeLisible} (
                      {dureeTotalEcheances}{" "}
                      {newPlan.frequence_paiement === "hebdomadaire"
                        ? "semaines"
                        : "jours"}
                      )<br />
                      <strong>Date de fin estimée :</strong>{" "}
                      {dateFin ? dateFin.toLocaleDateString("fr-FR") : "-"}
                    </div>
                  </Col>
                )}
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button 
                type="button" 
                onClick={toggleModal} 
                color="secondary" 
                disabled={adding}
              >
                Annuler
              </Button>
              <Button 
                type="submit" 
                color="primary" 
                disabled={adding} 
              >
                {adding ? (
                  <>
                    <span 
                      className="spinner-border spinner-border-sm me-2" 
                      role="status" 
                      aria-hidden="true"
                    ></span>
                    Ajout en cours...
                  </>
                  ) : (
                    <>
                      Enregistrer
                    </>
                  )
                }
              </Button>
            </ModalFooter>
          </Form>
        </Modal>
        <ToastContainer closeButton={false} />
      </Container>
    </div>
  );
};

Plans.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};

export default withRouter(withTranslation()(Plans));
